// Cross-checks between the saved cliente identity (nombreEmpresa / rfc) and
// values the AI extracts from expediente documents, contratos and fianzas.
// Pure data helpers — keep this file free of React imports.
//
// House rule (same as namesMatch): if either side is missing, there is no
// mismatch. Notices only fire on a real contradiction, never on absence.

import { namesMatch } from './convenioCompliance';
import { extractFieldValue } from './documentExpiry';

// Which extracted keys identify the cliente empresa, per fileType.
// Keys are inconsistent across prompts (legacy PascalCase vs SUR_* camelCase),
// so this map is the single source of truth for identity comparison.
// Deliberately absent: identificacionOficial / identificacionDuenos (a person,
// not the empresa), estadosFinancieros* (no identity fields), and every
// obligados_solidarios / bienes_inmuebles doc type (they describe a different
// entity than the cliente).
export const IDENTITY_KEYS = {
  actaConstitutiva: { name: 'CompanyName' },
  constanciaSituacionFiscal: { name: 'CompanyLegalName', rfc: 'RFC' },
  csf: { name: 'CompanyLegalName', rfc: 'RFC' },
  comprobanteDomicilio: { name: 'CompanyName' },
  acuseReciboDeclaracion: { name: 'CompanyName', rfc: 'RFC' },
  declaracionEjercicio: { name: 'CompanyName', rfc: 'RFC' },
  declaracionAnual: { name: 'CompanyName', rfc: 'RFC' },
  curriculumEmpresarial: { name: 'NombreEmpresa' },
  revisionBuro: { name: 'razonSocial', rfc: 'rfc' },
  entrevista: { name: 'razonSocial', rfc: 'rfc' },
  // Cliente contracts only: the proveedor IS the cliente. Callers in the
  // convenio flow must not pass cliente identity for contrato docs (there the
  // proveedor is a different entity). The contrato prompt extracts no RFC.
  contrato: { name: 'proveedor', nameLabel: 'Proveedor' },
};

// Canonical RFC form: uppercase, no spaces, hyphens or stray punctuation.
// Stored clientes/{id}.rfc predates auto-caps, so both sides get normalized.
export function normalizeRfc(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw.toUpperCase().replace(/[^A-ZÑ&0-9]/g, '');
}

export function rfcsMatch(a, b) {
  const na = normalizeRfc(a);
  const nb = normalizeRfc(b);
  if (!na || !nb) return true;
  return na === nb;
}

// Compares one document's extracted data against the saved cliente identity.
// Returns [] when everything matches (or nothing is comparable), otherwise
// [{ field: 'razonSocial'|'rfc', fieldKey, saved, extracted }].
export function getIdentityMismatches(fileType, extractedData, cliente) {
  const keys = IDENTITY_KEYS[fileType];
  if (!keys || !extractedData || !cliente) return [];

  const mismatches = [];

  if (keys.name && cliente.nombreEmpresa) {
    const extractedName = extractFieldValue(extractedData, keys.name);
    if (
      extractedName &&
      typeof extractedName === 'string' &&
      !namesMatch(cliente.nombreEmpresa, extractedName)
    ) {
      mismatches.push({
        field: 'razonSocial',
        fieldKey: keys.name,
        label: keys.nameLabel || 'Razón social',
        saved: cliente.nombreEmpresa,
        extracted: extractedName,
      });
    }
  }

  if (keys.rfc && cliente.rfc) {
    const extractedRfc = extractFieldValue(extractedData, keys.rfc);
    if (
      extractedRfc &&
      typeof extractedRfc === 'string' &&
      !rfcsMatch(cliente.rfc, extractedRfc)
    ) {
      mismatches.push({
        field: 'rfc',
        fieldKey: keys.rfc,
        label: 'RFC',
        saved: cliente.rfc,
        extracted: extractedRfc,
      });
    }
  }

  return mismatches;
}

// Scans the ClienteDetails processedByFile aggregation
// ({ [fileType]: [{data, createdAt, ...versions}] }) and returns one entry per
// document type with contradictions, for the amber "Datos que no coinciden"
// panel. Only the most recent version of each document is checked — older
// superseded uploads may legitimately differ.
export function getClientIdentityMismatches(processedByFile, cliente) {
  if (!processedByFile || !cliente) return [];
  const results = [];
  for (const [fileType, versions] of Object.entries(processedByFile)) {
    if (!versions || versions.length === 0) continue;
    const mostRecent = [...versions].sort((a, b) => b.createdAt - a.createdAt)[0];
    if (!mostRecent?.data) continue;
    const mismatches = getIdentityMismatches(fileType, mostRecent.data, cliente);
    if (mismatches.length > 0) {
      results.push({ fileType, mismatches });
    }
  }
  return results;
}

// Table-level aggregate for the Clientes list, mirroring the data access of
// getClientExpiryStatus (documentExpiry.js): walks linksById[].filesData and
// counts document types whose most recent processed version contradicts the
// saved cliente identity.
export function getClientIdentityStatus(cliente, clientLinkIds, linksById) {
  const result = { mismatchCount: 0, fileTypes: [] };
  if (!cliente || !clientLinkIds || !linksById) return result;

  // Most recent processed results per fileType across all the cliente's links.
  const mostRecentByType = {};
  for (const linkId of clientLinkIds) {
    const link = linksById[linkId];
    if (!link?.filesData) continue;
    for (const [fileDocId, fileDoc] of Object.entries(link.filesData)) {
      if (!fileDoc?.results || fileDoc.status !== 'procesado') continue;
      // fileDocId format: "fileType_timestamp"
      const underscoreIdx = fileDocId.indexOf('_');
      if (underscoreIdx === -1) continue;
      const fileType = fileDocId.substring(0, underscoreIdx);
      if (!IDENTITY_KEYS[fileType]) continue;
      const timestamp = fileDocId.substring(underscoreIdx + 1);
      const existing = mostRecentByType[fileType];
      if (!existing || timestamp > existing.timestamp) {
        mostRecentByType[fileType] = { timestamp, results: fileDoc.results };
      }
    }
  }

  for (const [fileType, { results }] of Object.entries(mostRecentByType)) {
    if (getIdentityMismatches(fileType, results, cliente).length > 0) {
      result.mismatchCount++;
      result.fileTypes.push(fileType);
    }
  }
  return result;
}

// Fianza XML side: compares the cliente against xmlFianza.fiado {nombre, rfc}.
// Same permissive-on-missing rule.
export function getFiadoIdentityMismatches(cliente, fiado) {
  if (!cliente || !fiado) return [];
  const mismatches = [];
  if (cliente.nombreEmpresa && fiado.nombre && !namesMatch(cliente.nombreEmpresa, fiado.nombre)) {
    mismatches.push({
      field: 'razonSocial',
      saved: cliente.nombreEmpresa,
      extracted: fiado.nombre,
    });
  }
  if (cliente.rfc && fiado.rfc && !rfcsMatch(cliente.rfc, fiado.rfc)) {
    mismatches.push({ field: 'rfc', saved: cliente.rfc, extracted: fiado.rfc });
  }
  return mismatches;
}
